/**
 * Agencies — every tenant on the platform.
 *
 * The comp's Plan column is real now that subscriptions exist. MRR and Seats
 * stay out: there is still no revenue rollup or seat model, and inventing
 * figures in a directory of real customers is worse than an honest omission.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowSquareOut,
  Buildings,
  MagnifyingGlass,
  PlayCircle,
  Prohibit,
} from '@phosphor-icons/react';
import {
  useAgencies,
 
  useSetAgencyStatus,
  type AgencyFilters,
} from '../queries/platform';
import type { Agency } from '../lib/types';
import { apiError } from '../lib/api';
import { inr, relativeDate } from '../lib/format';
import {
  Avatar,
  Badge,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Segmented,
  StatusDot,
  type Tone,
} from '../components/ui';

const COLUMNS = '2fr 1.2fr 1.1fr 1.1fr .9fr 1.4fr';

export function agencyState(agency: Agency): { label: string; tone: Tone } {
  if (agency.status === 'inactive') return { label: 'Suspended', tone: 'bad' };
  return { label: 'Active', tone: 'ok' };
}

/**
 * The billing state at a glance. Resolved here for display only — the API is
 * the authority, and this must never be the thing that decides access.
 */
function PlanCell({ agency }: { agency: Agency }) {
  const sub = agency.subscription;
  if (!sub) return <span className="t-sm faint">No plan</span>;

  const now = Date.now();
  const trialEnds = sub.trialEndsAt ? new Date(sub.trialEndsAt).getTime() : null;
  const periodEnds = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).getTime() : null;

  if (sub.status === 'trialing' && trialEnds && now < trialEnds) {
    const days = Math.ceil((trialEnds - now) / 86_400_000);
    return (
      <div>
        <Badge tone={days <= 7 ? 'warn' : 'info'}>Trial</Badge>
        <p className="t-xs faint" style={{ margin: '4px 0 0' }}>
          {days}d left
        </p>
      </div>
    );
  }

  if (sub.status === 'active' && (!periodEnds || now < periodEnds)) {
    return (
      <div>
        <span className="num" style={{ fontSize: 13.5 }}>
          {inr(sub.amount ?? 0)}
        </span>
        <p className="t-xs faint" style={{ margin: '3px 0 0' }}>
          {sub.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
          {sub.addOns?.length ? ` · +${sub.addOns.length}` : ''}
        </p>
      </div>
    );
  }

  return <Badge tone="bad">Unpaid</Badge>;
}

export default function Agencies() {
  const [filters, setFilters] = useState<AgencyFilters>({ status: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const { data, isPending, isError, error: loadError, refetch } = useAgencies(filters);
  const setStatus = useSetAgencyStatus();

  const rows = data?.data ?? [];
  const counts = data?.counts;

  const toggle = async (agency: Agency) => {
    setError('');
    setNotice('');
    try {
      const next = agency.status === 'active' ? 'inactive' : 'active';
      await setStatus.mutateAsync({ id: agency._id, status: next });
      setNotice(
        next === 'inactive'
          ? `${agency.name} suspended. They can no longer sign in.`
          : `${agency.name} reinstated.`,
      );
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="animate-up">
      {(notice || error) && (
        <div
          className="preview-note"
          style={{
            borderStyle: 'solid',
            borderColor: error ? 'var(--bad-line)' : 'var(--ok-line)',
            background: error ? 'var(--bad-bg)' : 'var(--ok-bg)',
            color: error ? 'var(--bad)' : 'var(--ok)',
          }}
        >
          <span>{error || notice}</span>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <Segmented
            value={filters.status}
            onChange={(status) => setFilters((f) => ({ ...f, status }))}
            options={[
              { id: 'all', label: 'All', count: counts?.all },
              { id: 'active', label: 'Active', count: counts?.active },
              { id: 'inactive', label: 'Suspended', count: counts?.inactive },
            ]}
          />
          <div className="spacer" />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFilters((f) => ({ ...f, search: searchInput.trim() }));
            }}
            style={{ position: 'relative', width: 250 }}
          >
            <MagnifyingGlass
              size={15}
              color="var(--faint)"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              className="input"
              style={{ paddingLeft: 35 }}
              placeholder="Search name, phone, email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </form>
        </div>

        {isPending ? (
          <LoadingBlock label="Loading agencies…" />
        ) : isError ? (
          <ErrorState message={apiError(loadError)} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Buildings size={24} />}
            title={filters.search ? 'No agency matches that' : 'No agencies yet'}
            message={
              filters.search
                ? 'Try a different name, phone or email.'
                : 'Approve a registration, or onboard one directly.'
            }
            action={
              !filters.search && (
                <Link className="btn btn-primary" to="/onboard">
                  Onboard an agency
                </Link>
              )
            }
          />
        ) : (
          <div className="table-scroll">
            <div>
              <div className="table-head" style={{ gridTemplateColumns: COLUMNS }}>
                <span>Agency</span>
                <span>Contact</span>
                <span>Status</span>
                <span>Plan</span>
                <span>Joined</span>
                <span style={{ textAlign: 'right' }}>Actions</span>
              </div>

              {rows.map((agency) => {
                const state = agencyState(agency);
                const suspended = agency.status === 'inactive';
                return (
                  <div key={agency._id} className="table-row" style={{ gridTemplateColumns: COLUMNS }}>
                    <Link
                      to={`/agencies/${agency._id}`}
                      className="row"
                      style={{ minWidth: 0, color: 'inherit' }}
                    >
                      <Avatar name={agency.name} />
                      <div style={{ minWidth: 0 }}>
                        <p className="truncate strong" style={{ margin: 0, fontSize: 13.5 }}>
                          {agency.name}
                        </p>
                        <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                          {agency.email || 'No email'}
                        </p>
                      </div>
                    </Link>

                    <div style={{ minWidth: 0 }}>
                      <p className="truncate t-md" style={{ margin: 0 }}>
                        {agency.contactName || 'Not recorded'}
                      </p>
                      <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                        {agency.phone}
                      </p>
                    </div>

                    <div className="row row-tight">
                      <StatusDot tone={state.tone} />
                      <span className="t-sm strong">{state.label}</span>
                    </div>

                    <PlanCell agency={agency} />

                    <span className="t-sm muted">{relativeDate(agency.createdAt)}</span>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
                      <Link className="btn btn-icon" to={`/agencies/${agency._id}`} title="Open">
                        <ArrowSquareOut size={15} />
                      </Link>
                      <button
                        className={`btn btn-sm ${suspended ? 'btn-ok' : 'btn-bad'}`}
                        onClick={() => toggle(agency)}
                        disabled={setStatus.isPending}
                      >
                        {suspended ? <PlayCircle size={14} /> : <Prohibit size={14} />}
                        {suspended ? 'Activate' : 'Suspend'}
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="table-foot">
                <p className="t-sm muted" style={{ margin: 0 }}>
                  Showing {rows.length}
                  {counts && filters.status !== 'all' ? ` of ${counts.all} total` : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
